import 'intersection-observer'
import * as React from 'react'
import { InView } from 'react-intersection-observer'
import { IOWidget, Updater, fromJSX } from 'widgets-for-react'
import { blockInviewHandler } from '../../../drupal/rendering/inview_utils.mod'
import { CMSState, Id, ImageSize, Link } from '../types.gen'
import { getClassName, link } from '../utils.gen'
import { ModernImageContainer } from './ImageContainer.gen'

export type ButtonProps = ({ kind: 'button'; on_click: () => Updater<CMSState> } | Link) & { title: string }

export type FeaturedContentItemProps = {
  image: Id
  image_size: ImageSize
  image_alt_text: string
  label?: string
  title: React.ReactNode
  subtitle?: React.ReactNode
  intro?: string
  link: Link
  link_label?: string
  get_default_data_src: () => ImageSize
  get_default_data_srcset: () => [ImageSize, string][]
}

export type FeaturedContentBlockProps = {
  index: number
  block_index: number
  imageLocation: 'left' | 'right'
  featuredItem: FeaturedContentItemProps
  inview?: {
    threshold?: number
    triggerOnce?: boolean
  }
  copy: FeaturedContentBlockCopy
}

export type FeaturedContentBlockCopy = {
  more: string
}

export type FeaturedContentBlockStyle = Partial<{
  block_classname: string
  inview_classname: string
  inner_classname: string
  content_classname: string
  header_classname: string
  label_classname: string
  title_classname: string
  subtitle_classname: string
  text_classname: string
  intro_classname: string
  footer_classname: string
  clickable_wrapper_classname: string
  clickable_primary_classname: string
  clickable_secondary_classname: string
  figure_classname: string
  image_classname: string
}>

export function FeaturedContentBlock(
  props: FeaturedContentBlockProps,
  options?: { style?: FeaturedContentBlockStyle; key?: string }
): IOWidget<CMSState, Updater<CMSState>> {
  return (cmsState) => {
    return fromJSX(() => {
      return props.featuredItem.title && props.featuredItem.intro && props.featuredItem.image ? (
        <InView
          as="section"
          onChange={(inView) =>
            blockInviewHandler(
              'featuredContent',
              inView,
              `io-${options && options.key ? options.key : props.index}`,
              getClassName(options, 'inview_classname', 'inview')
            )
          }
          triggerOnce={props.inview && props.inview.triggerOnce ? props.inview.triggerOnce : false}
          threshold={props.inview && props.inview.threshold ? props.inview.threshold : 0.1}
          id={`io-${options && options.key ? options.key : props.index}`}
          className={getClassName(options, 'block_classname', 'container')}
          key={options && options.key ? options.key : `block-${props.index}`}
        >
          <div className={getClassName(options, 'inner_classname', 'py-5 row')}>
            <div
              className={getClassName(
                options,
                'content_classname',
                `col-md-6 mb-4 order-${props.imageLocation == 'left' ? 2 : 1}`
              )}
            >
              <header className={getClassName(options, 'header_classname', '')}>
                {props.featuredItem.label && (
                  <span className={getClassName(options, 'label_classname', 'd-block small')}>
                    {props.featuredItem.label}
                  </span>
                )}
                <h2 className={getClassName(options, 'title_classname', 'mb-3')}>{props.featuredItem.title}</h2>
                {props.featuredItem.subtitle && (
                  <p className={getClassName(options, 'subtitle_classname', 'mb-3 text-muted')}>
                    {props.featuredItem.subtitle}
                  </p>
                )}
              </header>

              {props.featuredItem.intro && (
                <div className={getClassName(options, 'text_classname', '')}>
                  <p className={getClassName(options, 'intro_classname', '')}>{props.featuredItem.intro}</p>
                </div>
              )}

              <footer className={getClassName(options, 'footer_classname', '')}>
                <div className={getClassName(options, 'clickable_wrapper_classname', '')}>
                  {props.featuredItem.link &&
                    link(
                      props.featuredItem.link,
                      { classname: getClassName(options, 'clickable_primary_classname', 'btn btn-dark') },
                      props.featuredItem.link_label ? props.featuredItem.link_label : props.copy.more
                    )}
                </div>
              </footer>
            </div>

            <div
              className={getClassName(
                options,
                'figure_classname',
                `col-md-6 mb-4 order-${props.imageLocation == 'left' ? 1 : 2}`
              )}
            >
              {cmsState.images.has(props.featuredItem.image) &&
                ModernImageContainer(
                  cmsState.images.get(props.featuredItem.image),
                  getClassName(options, 'image_classname', 'w-100'),
                  props.featuredItem.image_alt_text,
                  props.featuredItem.get_default_data_src,
                  props.featuredItem.get_default_data_srcset
                ).run((_) => {})}
            </div>
          </div>
        </InView>
      ) : (
        <></>
      )
    })
  }
}
